import {HiOutlineDuplicate, HiOutlinePencil, HiOutlineTrash} from 'react-icons/hi';
import {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {initCategory} from 'utils/initData';
import SpinnerComponent from 'components/spinner';
import {defaultpageCount, permissionKeys, sortByTypeType} from 'utils/constants';
import {isEqual} from 'lodash';
import NoResult from 'components/commonComponent/noResult';
import {toast} from 'react-toastify';
import {checkPermission, copyToClipboard, messageErrors} from '../../utils/utils';
import {useSelector} from 'react-redux';
import ActionTable from 'components/table/actionTable';
import {orgSelector, userSelector} from 'redux/selectors';
import useUser from 'hooks/useUser';
import '../../styles/styles.scss';
import './category.scss';
import Sort from 'components/table/sort';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import EmptyState from 'components/commonComponent/emptyState';
import {deleteCategoryById, getCategoriesByFilter} from 'api/categoryApi';
import CategoryModal from 'components/modal/categoryModal/categoryModal';
import ConfirmModal from 'components/modal/confirmModal';
import {TreeTable} from 'primereact/treetable';
import {Column} from 'primereact/column';
import CustomPaginatorTreeTable from 'components/table/customPaginateTreeTable';
import {PaginatorTemplate} from 'primereact/paginator';

const categoryEmpty = require('../../assets/image/svg/document-text.svg').default || '';

const CategoryList = (props: any) => {
    const queryClient = useQueryClient();
    const {WRITE_CATEGORY} = permissionKeys;
    const [t] = useTranslation();
    const {userInfo} = useSelector(userSelector);
    const [searchValue, setSearchValue] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [openModalConfirm, setOpenModalConfirm] = useState(false);
    const [idDelete, setIdDelete] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [categories, setCategories] = useState([initCategory]);
    const [targetCategory, setTargetcategory] = useState(initCategory);
    const [sortBy, setSortBy] = useState('');
    const [sortByType, setSortByType] = useState('');
    const {organizationId} = useSelector(orgSelector);
    const isWriteCategory = checkPermission(userInfo, props.type, [WRITE_CATEGORY], organizationId);
    const [nodes, setNodes] = useState<any[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<any>({});
    const [totalEntities, setTotalEntities] = useState(0);
    const [cacheBust, setCacheBust] = useState(() => Date.now());

    const handleAddCategory = () => {
        setOpenModal(true);
        setIsEdit(false);
    };

    const {data, isLoading, refetch, error} = useQuery(
        ['getCategories', searchValue, sortBy, sortByType, organizationId, cacheBust],
        () => getCategoriesByFilter({
            page: 0,
            limit: 1000,
            searchQuery: searchValue,
            sortBy,
            sortByType,
            organizationId,
            _t: cacheBust,
        } as any),
        {staleTime: Infinity},
    );

    const handleSearch = (value: any) => {
        setSearchValue(value);
    };

    const handleCopyToClipboard = (e: any, item: any) => {
        copyToClipboard(item.name, t('categoryPage.copiedName'));
        e.stopPropagation();
    };

    const handleHeaderSort = (field: string) => {
        setSortBy(field);
        let type = '';
        if (field !== sortBy) {
            type = sortByTypeType.ASC;
        } else if (sortByType === sortByTypeType.ASC) {
            type = sortByTypeType.DESC;
        } else {
            setSortBy(type);
        }
        setSortByType(type);
    };

    const handleEdit = async (item: any) => {
        if (isWriteCategory) {
            setTargetcategory(item);
            setOpenModal(true);
            setIsEdit(true);
        }
    };

    const handleDelete = async (id: string) => {
        setIdDelete(id);
        setOpenModalConfirm(!openModalConfirm);
    };

    const onDelete = async (id: string) => {
        try {
            const response = await deleteCategoryById(id);
            if (response.status === 200) {
                await queryClient.invalidateQueries(['getCategories']);
                await queryClient.invalidateQueries(['getCategoriesForParentSelect']);
                toast.success(t('categoryPage.deleteSuccessfully') as string);
                setOpenModalConfirm(false);
                setCacheBust(Date.now());
            }
        } catch (error: any) {
            const message: string = `${error.response.data.errors?.[0].detail}`;
            toast.error(message);
        }
    };

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const buildTreeNodes = (flatData: any[]): any[] => {
        const nodeMap: Record<string, any> = {};
        flatData.forEach((item: any) => {
            nodeMap[item.id] = {key: item.id, data: item, children: []};
        });
        const roots: any[] = [];
        flatData.forEach((item: any) => {
            const parentId = item.parentId || item.parent?.id;
            if (parentId && nodeMap[parentId]) {
                nodeMap[parentId].children.push(nodeMap[item.id]);
            } else {
                roots.push(nodeMap[item.id]);
            }
        });
        return roots;
    };

    useEffect(() => {
        if (data) {
            const entities = data?.data?.entities || [];
            setCategories(entities);
            setTotalEntities(data.data?.totalEntities || 0);
            setNodes(buildTreeNodes(entities));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const expandNode = (key: any) => {
        const _expandedKeys = {...expandedKeys};
        if (_expandedKeys[key]) delete _expandedKeys[key];
        else _expandedKeys[key] = true;
        setExpandedKeys(_expandedKeys);
    };

    const actionTemplate = (_key: string, item: any) => {
        if (!isWriteCategory) return null;
        return (
            <GroupButtonWithIcons
                className="items-center justify-end"
                buttons={[
                    {
                        type: 'button',
                        text: '',
                        classType: 'white',
                        icon: <HiOutlinePencil size={'20px'}/>,
                        action: () => handleEdit(item),
                    },
                    {
                        type: 'submit',
                        text: '',
                        classType: 'white',
                        icon: <HiOutlineTrash size={'20px'}/>,
                        action: () => handleDelete(item?.id),
                    },
                ]}
            />
        );
    };

    const categoryHeaderTemplate = () => (
        <div className="flex items-center text-xs uppercase text-gray-900 font-medium cursor-pointer"
             onClick={() => handleHeaderSort('name')}>
            {t('categoryPage.category')}
            <Sort check={sortBy === 'name'} sortByType={sortByType}/>
        </div>
    );

    const categoryNameTemplate = (item: any) => (
        <div className="flex flex-row items-center" onClick={() => handleEdit(item)}>
            <div className="text-secondary text-sm cursor-pointer group-name">{item?.name}</div>
            <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                <HiOutlineDuplicate size={25} onClick={(e: any) => handleCopyToClipboard(e, item)}/>
            </div>
        </div>
    );

    const itemCountHeaderTemplate = () => (
        <div className="flex items-center text-xs uppercase text-gray-900 font-medium cursor-pointer"
             onClick={() => handleHeaderSort('item')}>
            {t('categoryPage.items')}
            <Sort check={sortBy === 'item'} sortByType={sortByType}/>
        </div>
    );

    const itemCountTemplate = (item: any) => (
        <div className="text-black text-sm cursor-pointer" onClick={() => handleEdit(item)}>
            {item?.countItem ?? 0}
        </div>
    );

    if (isLoading) return null;

    return (
        <>
            {error
                ? <EmptyState
                    buttonName={t('categoryPage.addCategory')}
                    handleAddClick={isWriteCategory && handleAddCategory}
                    title={t('categoryPage.titleNoResult')}
                    subtitle={t('categoryPage.subTitleNoResult')}
                    icon={categoryEmpty}
                />
                : <>
                    {!isEqual(categories[0], initCategory) && totalEntities === 0 && searchValue === ''
                        ? <EmptyState
                            buttonName={t('categoryPage.addCategory')}
                            handleAddClick={isWriteCategory && handleAddCategory}
                            title={t('categoryPage.titleNoResult')}
                            subtitle={t('categoryPage.subTitleNoResult')}
                            icon={categoryEmpty}
                        />
                        : !isEqual(categories[0], initCategory) && totalEntities === 0
                            ? (
                                <div className="p-[30px]">
                                    <ActionTable
                                        placeholderSearch="Search"
                                        buttonName={t('categoryPage.addCategory')}
                                        handleAddClick={isWriteCategory && handleAddCategory}
                                        handleSearch={handleSearch}
                                        searchValue={searchValue}
                                    />
                                    <NoResult/>
                                </div>
                            )
                            : (
                                <div className="main-sub">
                                    <ActionTable
                                        placeholderSearch="Search"
                                        buttonName={t('categoryPage.addCategory')}
                                        handleAddClick={isWriteCategory && handleAddCategory}
                                        handleSearch={handleSearch}
                                    />
                                    <TreeTable
                                        value={nodes}
                                        expandedKeys={expandedKeys}
                                        onToggle={(e) => setExpandedKeys(e.value)}
                                        onRowClick={(e: any) => expandNode(e.node.key)}
                                        tableStyle={{minWidth: '100%'}}
                                        className="border-b"
                                        paginator
                                        rows={defaultpageCount}
                                        paginatorTemplate={<CustomPaginatorTreeTable/> as PaginatorTemplate}
                                    >
                                        <Column
                                            field="name"
                                            header={categoryHeaderTemplate}
                                            body={(node: any) => categoryNameTemplate(node.data)}
                                            bodyClassName="text-secondary text-sm cursor-pointer hover:bg-gray-50 group-name"
                                            expander
                                        />
                                        <Column
                                            field="countItem"
                                            header={itemCountHeaderTemplate}
                                            body={(node: any) => itemCountTemplate(node.data)}
                                            style={{width: '150px'}}
                                        />
                                        <Column
                                            body={(node: any) => actionTemplate(node.key, node.data)}
                                            style={{width: '120px'}}
                                        />
                                    </TreeTable>
                                    {isLoading && <SpinnerComponent/>}
                                </div>
                            )
                    }
                </>
            }
            {openModal && (
                <CategoryModal
                    headerTitle={isEdit ? t('categoryPage.editCategory') : t('categoryPage.addCategory')}
                    openModal={openModal}
                    isEdit={isEdit}
                    targetData={targetCategory}
                    setOpenModal={setOpenModal}
                    queryClient={queryClient}
                    setCurrentPage={() => setCacheBust(Date.now())}
                    currentPage={0}
                />
            )}
            {openModalConfirm && (
                <ConfirmModal
                    openModal={openModalConfirm}
                    setOpenModal={setOpenModalConfirm}
                    onDelete={() => onDelete(idDelete)}
                />
            )}
        </>
    );
};

export default CategoryList;
